import {
  TUser,
  TUserContactInfo,
  TUserDashboard,
  TUserDemographicsInfoWithoutPicture,
  TUserLocation,
} from '@/types/User.types';
import { demographicsFeToBe, userBeToFe } from '@/utils/demographicDataMappers';
import { api, TCustomAxiosResponse } from '..';
import { WELLNESS_DATA_MULTIPLIER } from '@/constants';
import { THealthDomainScores } from '@/types/Health.types';
import { TNotificationChannel } from '@/types/Notification.types';

const rootPath = '/v1/user';

const endpoints = {
  user: `${rootPath}`,
  userLocation: `${rootPath}/location`,
  userDashboard: `${rootPath}/dashboard`,
  signIn: `${rootPath}/login`,
  signOut: `${rootPath}/logout`,
  forgotPassword: `${rootPath}/forgot-password`,
  uploadProfilePic: `${rootPath}/upload-profile-picture`,
  setInitialPassword: `${rootPath}/set-password`,
  resetPassword: `${rootPath}/reset-password`,
  verifyAuthToken: `${rootPath}/verify-token`,
  verifyResetAuthToken: `${rootPath}/verify-reset-token`,
  deleteOwnAccount: `${rootPath}/me`,
  notificationPreferences: `${rootPath}/notification-preferences`,
};

export const userGet = async (): Promise<TUser> => {
  const { data } = await api.get(endpoints.user);
  const user = userBeToFe(data.data) as TUser;

  return user;
};

export const userDashboardGet = async (): Promise<TUserDashboard> => {
  const { data } = await api.get<{ data: TUserDashboard }>(endpoints.userDashboard);

  const wellnessDomainsMultiplied = data.data.score.domains;

  for (const key in wellnessDomainsMultiplied) {
    if (typeof wellnessDomainsMultiplied[key as keyof THealthDomainScores] == 'number' && key !== 'totalScore') {
      wellnessDomainsMultiplied[key as keyof THealthDomainScores] *= WELLNESS_DATA_MULTIPLIER;
    }
  }

  return {
    ...data.data,
    score: {
      ...data.data.score,
      domains: wellnessDomainsMultiplied,
    },
  };
};

export const userNotificationPreferencesGet = async (): Promise<TNotificationChannel[]> => {
  const { data } = await api.get<{ channels: TNotificationChannel[] }>(endpoints.notificationPreferences);

  return data.channels;
};

export const updateUserNotificationPreferencesPatch = async (
  newNotificationPreferences: TNotificationChannel[],
): Promise<string> => {
  const { data } = await api.patch<TCustomAxiosResponse<string>>(endpoints.notificationPreferences, {
    channels: newNotificationPreferences,
  });

  return data.data;
};

export const signInPost = async (email: string, password: string): Promise<TUser> => {
  const res = await api.post(endpoints.signIn, {
    email,
    password,
  });

  return userBeToFe(res.data.data);
};

export const signOutPost = async (): Promise<TUser> => {
  const res = await api.post(`${import.meta.env.VITE_API_BASE_URL}${endpoints.signOut}`);

  return res.data.data;
};

export const forgotPasswordPost = async (email: string) => {
  await api.post(endpoints.forgotPassword, {
    email,
  });

  return 'ok';
};

export const updateUserNSubmitEulaPut = async (info: TUserContactInfo): Promise<TUser> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { email, ...restForm } = info;

  const { data } = await api.put<TCustomAxiosResponse<TUser>>(endpoints.user, {
    ...restForm,
  });

  const user = data.data;

  return user;
};

export const updateUserDemoGraphicsPut = async (info: TUserDemographicsInfoWithoutPicture): Promise<TUser> => {
  const userDemographicsBeMapped = demographicsFeToBe(info);

  const { data } = await api.put<TCustomAxiosResponse<TUser>>(endpoints.user, {
    ...userDemographicsBeMapped,
  });

  const user = userBeToFe(data.data);
  return user;
};

export const updateUserLocationPatch = async (location: TUserLocation): Promise<TUser> => {
  const { data } = await api.patch<TCustomAxiosResponse<TUser>>(endpoints.userLocation, {
    ...location,
  });

  const user = userBeToFe(data.data);
  return user;
};

export const uploadProfilePhoto = async (photo: File): Promise<TUser> => {
  const formData = new FormData();
  formData.append('file', new File([photo], photo.name));

  const { data } = await api.post(endpoints.uploadProfilePic, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return userBeToFe(data.data);
};

export const updateUserContactDetails = async (userContactDetails: Omit<TUserContactInfo, 'email'>): Promise<TUser> => {
  const { data } = await api.put(endpoints.user, userContactDetails);

  const updatedUser: TUser = userBeToFe(data.data);
  return updatedUser;
};

export const verifyAuthToken = async (body: { token: string; email: string }): Promise<{ success: boolean }> => {
  const { data } = await api.post(endpoints.verifyAuthToken, body);
  return data.data;
};

export const verifyResetAuthToken = async (body: { token: string; email: string }): Promise<{ success: boolean }> => {
  const { data } = await api.post(endpoints.verifyResetAuthToken, body);
  return data.data;
};

export const setInitialUserPassword = async (body: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<{ success: boolean }> => {
  const { data } = await api.post(endpoints.setInitialPassword, body);
  return data.data;
};

export const resetUserPassword = async (body: {
  email: string;
  token: string;
  newPassword: string;
}): Promise<{ success: boolean }> => {
  const { data } = await api.post(endpoints.resetPassword, body);
  return data.data;
};

export const deleteOwnAccount = async (): Promise<{ success: boolean }> => {
  const { data } = await api.delete(endpoints.deleteOwnAccount);
  return data.data;
};
